import React from 'react';
import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalPoliceOffenseItemModel } from 'forms/sections/legal/police/offense/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import {
  DateModel, FieldModel, LocationModel, YesOrNoModel
} from 'forms/shared/types';
import Charge from './Charge';
import Sentence from './Sentence';

interface Props extends Partial<LegalPoliceOffenseItemModel>, ValidationElementProps {
  onUpdate: (item: Partial<LegalPoliceOffenseItemModel>) => void,
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  defaultState?: boolean
  formType?: string
  requireLegalOffenseInvolvements?: boolean
  requireLegalOffenseSentenced?: boolean
  requireLegalOffenseIncarcerated?: boolean
  continuation?: boolean
  errors?: any
}

export default class Offense extends ValidationElement<Props> {
  update = (queue: Partial<LegalPoliceOffenseItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Description: this.props.Description,
      InvolvedViolence: this.props.InvolvedViolence,
      InvolvedExplosives: this.props.InvolvedExplosives,
      InvolvedFirearms: this.props.InvolvedFirearms,
      InvolvedSubstances: this.props.InvolvedSubstances,
      InvolvedAlcohol: this.props.InvolvedAlcohol,
      Address: this.props.Address,
      WasCited: this.props.WasCited,
      CitedBy: this.props.CitedBy,
      AgencyAddress: this.props.AgencyAddress,
      WasCharged: this.props.WasCharged,
      OtherLawEnforcementAgency: this.props.OtherLawEnforcementAgency,
      Explanation: this.props.Explanation,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Charges: this.props.Charges,
      WasSentenced: this.props.WasSentenced,
      Sentence: this.props.Sentence,
      AwaitingTrial: this.props.AwaitingTrial,
      AwaitingTrialExplanation: this.props.AwaitingTrialExplanation,
      ArrestingAgencyName: this.props.ArrestingAgencyName,
      ArrestingAgencyAddress: this.props.ArrestingAgencyAddress,
      CourtStatus: this.props.CourtStatus,
      CourtFinePayment: this.props.CourtFinePayment,
      CourtFineAmount: this.props.CourtFineAmount,
      CourtFinePaidInFull: this.props.CourtFinePaidInFull,
      CourtFinePaidInFullDate: this.props.CourtFinePaidInFullDate,
      CourtFineNotPaidInFullExplanation: this.props.CourtFineNotPaidInFullExplanation,
      CourtToAppear: this.props.CourtToAppear,
      CourtToAppearAddress: this.props.CourtToAppearAddress,
      /* Moved from Sentence */
      CaseClosed: this.props.CaseClosed,
      CaseClosedDate: this.props.CaseClosedDate,
      CaseClosedStatusExplanation: this.props.CaseClosedStatusExplanation,
      /* Moved from sentence */

      ...queue,
    });
  };

  updateDate = (values: LegalPoliceOffenseItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDescription = (values: LegalPoliceOffenseItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateInvolvedViolence = (values: LegalPoliceOffenseItemModel['InvolvedViolence']) => {
    this.update({
      InvolvedViolence: values,
    });
  };

  updateInvolvedFirearms = (values: LegalPoliceOffenseItemModel['InvolvedFirearms']) => {
    this.update({
      InvolvedFirearms: values,
    });
  };

  updateInvolvedExplosives = (values: LegalPoliceOffenseItemModel['InvolvedExplosives']) => {
    this.update({
      InvolvedExplosives: values,
    });
  };

  updateInvolvedSubstances = (values: LegalPoliceOffenseItemModel['InvolvedSubstances']) => {
    this.update({
      InvolvedSubstances: values,
    });
  };

  updateInvolvedAlcohol = (values: LegalPoliceOffenseItemModel['InvolvedAlcohol']) => {
    this.update({
      InvolvedAlcohol: values,
    });
  };

  updateAddress = (values: LegalPoliceOffenseItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateWasCited = (values: LegalPoliceOffenseItemModel['WasCited']) => {
    this.update({
      WasCited: values,
    });
  };

  updateCitedBy = (values: LegalPoliceOffenseItemModel['CitedBy']) => {
    this.update({
      CitedBy: values,
    });
  };

  updateAgencyAddress = (values: LegalPoliceOffenseItemModel['AgencyAddress']) => {
    this.update({
      AgencyAddress: values,
    });
  };

  updateWasCharged = (values: LegalPoliceOffenseItemModel['WasCharged']) => {
    this.update({
      WasCharged: values,
    });
  };

  updateOtherLawEnforcementAgency = (values: LegalPoliceOffenseItemModel['OtherLawEnforcementAgency']) => {
    this.update({
      OtherLawEnforcementAgency: values,
    });
  };

  updateArrestingAgencyName = (values: LegalPoliceOffenseItemModel['ArrestingAgencyName']) => {
    this.update({
      ArrestingAgencyName: values,
    });
  };

  updateArrestingAgencyAddress = (values: LegalPoliceOffenseItemModel['ArrestingAgencyAddress']) => {
    this.update({
      ArrestingAgencyAddress: values,
    });
  };

  updateCourtStatus = (courtStatus: LegalPoliceOffenseItemModel['CourtStatus']) => {
    const queue: Partial<LegalPoliceOffenseItemModel> = { CourtStatus: courtStatus };

    const value: string | undefined = courtStatus?.value;

    if (value === 'Yes') {
      if (this.props.CourtFinePayment?.value) {
        // Note: Using the "warning" attribute on Branch only works for "No"
        // eslint-disable-next-line no-alert
        if (!window.confirm(i18n.t('branch.confirmation'))) {
          return;
        }
      }

      // Clear the data that is only relevant when CourtStatus is "No"
      queue.CourtFinePayment = {} as YesOrNoModel;
      queue.CourtFineAmount = {} as FieldModel;
      queue.CourtFinePaidInFull = {} as YesOrNoModel;
      queue.CourtFinePaidInFullDate = {} as DateModel;
      queue.CourtFineNotPaidInFullExplanation = {} as FieldModel;
    } else if (value === 'No') {
      if (this.props.CourtToAppear?.value) {
        // eslint-disable-next-line no-alert
        if (!window.confirm(i18n.t('branch.confirmation'))) {
          return;
        }
      }

      // Clear the data that is only relevant when CourtStatus is "Yes"
      queue.CourtToAppear = {} as FieldModel;
      queue.CourtToAppearAddress = {} as LocationModel;
    }

    this.update(queue);
  };

  updateCourtToAppear = (values: LegalPoliceOffenseItemModel['CourtToAppear']) => {
    this.update({
      CourtToAppear: values,
    });
  };

  updateCourtToAppearAddress = (values: LegalPoliceOffenseItemModel['CourtToAppearAddress']) => {
    this.update({
      CourtToAppearAddress: values,
    });
  };

  updateCourtFinePayment = (values: LegalPoliceOffenseItemModel['CourtFinePayment']) => {
    this.update({
      CourtFinePayment: values,
    });
  };

  updateCourtFineAmount = (values: LegalPoliceOffenseItemModel['CourtFineAmount']) => {
    this.update({
      CourtFineAmount: values,
    });
  };

  updateCourtFinePaidInFull = (values: LegalPoliceOffenseItemModel['CourtFinePaidInFull']) => {
    this.update({
      CourtFinePaidInFull: values,
    });
  };

  updateCourtFinePaidInFullDate = (values: LegalPoliceOffenseItemModel['CourtFinePaidInFullDate']) => {
    this.update({
      CourtFinePaidInFullDate: values,
    });
  };

  updateCourtFineNotPaidInFullExplanation = (values: LegalPoliceOffenseItemModel['CourtFineNotPaidInFullExplanation']) => {
    this.update({
      CourtFineNotPaidInFullExplanation: values,
    });
  };

  updateExplanation = (values: LegalPoliceOffenseItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateCourtName = (values: LegalPoliceOffenseItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtAddress = (values: LegalPoliceOffenseItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateCharges = (values: LegalPoliceOffenseItemModel['Charges']) => {
    this.update({
      Charges: values,
    });
  };

  updateWasSentenced = (values: LegalPoliceOffenseItemModel['WasSentenced']) => {
    this.update({
      WasSentenced: values,
    });
  };

  updateSentence = (values: LegalPoliceOffenseItemModel['Sentence']) => {
    this.update({
      Sentence: values,
    });
  };

  updateCaseClosed = (values: LegalPoliceOffenseItemModel['CaseClosed']) => {
    this.update({
      CaseClosed: values,
    });
  };

  updateCaseClosedDate = (values: LegalPoliceOffenseItemModel['CaseClosedDate']) => {
    this.update({
      CaseClosedDate: values,
    });
  };

  updateCaseClosedStatusExplanation = (values: LegalPoliceOffenseItemModel['CaseClosedStatusExplanation']) => {
    this.update({
      CaseClosedStatusExplanation: values,
    });
  };

  updateAwaitingTrial = (values: LegalPoliceOffenseItemModel['AwaitingTrial']) => {
    this.update({
      AwaitingTrial: values,
    });
  };

  updateAwaitingTrialExplanation = (
    values: LegalPoliceOffenseItemModel['AwaitingTrialExplanation']
  ) => {
    this.update({
      AwaitingTrialExplanation: values,
    });
  };

  summary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const charge = itemProperties.CourtCharge && itemProperties.CourtCharge.value;
    const date = DateSummary(itemProperties.CourtDate);

    return Summary({
      type: i18n.t('legal.police.collection.summary.item'),
      index,
      left: charge,
      right: date,
      placeholder: i18n.t('legal.police.collection.summary.unknown'),
    });
  };

  render() {
    const {
      formType,
      requireLegalOffenseInvolvements,
      requireLegalOffenseSentenced,
      requireLegalOffenseIncarcerated,
      errors,
    } = this.props;

    const chargesErrors = errors && errors.filter((e) => e.indexOf('Charges.accordion') > -1);

    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(formType);
    const isPvqB = formIncludesPvqPartB(formType);
    const violenceLabel = isPvqB ? 'legal.pvq.police.label.violenceConcise' : 'legal.pvq.police.label.violence';
    const chargesOuterTitle = isPvqB ? 'legal.pvq.police.heading.chargeDetailsContinuation' : 'legal.pvq.police.heading.chargeDetails';
    const chargesInnerTitle = isPvqB ? 'legal.pvq.police.para.chargeDetailsContinuation' : 'legal.pvq.police.para.chargeDetails';
    const citedByLabel = isPvqB ? 'legal.pvq.police.heading.citedbyContinuation' : 'legal.pvq.police.heading.citedby';

    return isPvq ? (
      <div className="offense">
        <hr />
        <h3>Offense {`${(this.props.$index || 0) + 1}`}</h3>
        <Field
          title={i18n.t('legal.pvq.police.heading.date')} // 'What is the date of the offense?',
          help="legal.pvq.police.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            className="offense-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}

            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.pvq.police.heading.address')}
          attribute={`${attribute}.Address`}
        >
          <PVQLocation
            {...this.props.Address}
            name="Address"
            attribute={`${attribute}.Address`}

            label={i18n.t('legal.pvq.police.label.otherOffenseAddressUS')}
            countyLabel={i18n.t('legal.pvq.police.label.county')}
            layout={PVQLocation.US_COUNTY_CITY_STATE_ZIP_INTERNATIONAL_CITY_COUNTRY}
            geocode
            addressBook="Facility"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showInternationalStreet={false}
          />
        </Field>

        {requireLegalOffenseInvolvements && (
          <>
            <Branch
              {...this.props.InvolvedViolence}
              name="involved_violence"
              labelSize="h4"
              adjustFor="p"
              className="offense-violence no-margin-bottom"
              onUpdate={this.updateInvolvedViolence}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedViolence`}
            >
              {i18n.t(violenceLabel)}
            </Branch>

            <Branch
              {...this.props.InvolvedFirearms}
              name="involved_firearms"
              adjustFor="p"
              className="offense-firearms no-margin-bottom"
              onUpdate={this.updateInvolvedFirearms}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedFirearms`}
            >
              {i18n.t('legal.pvq.police.label.firearms')}
            </Branch>
            <Branch
              {...this.props.InvolvedExplosives}
              name="involved_explosives"
              adjustFor="p"
              className="offense-firearms no-margin-bottom"
              onUpdate={this.updateInvolvedExplosives}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedExplosives`}
            >
              {i18n.t('legal.pvq.police.label.explosives')}
            </Branch>

            <Branch
              {...this.props.InvolvedSubstances}
              name="involved_substances"
              adjustFor="p"
              className="offense-substances no-margin-bottom"
              onUpdate={this.updateInvolvedSubstances}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedSubstances`}
            >
              {i18n.t('legal.pvq.police.label.substances')}
            </Branch>
            <Branch
              {...this.props.InvolvedAlcohol}
              name="involved_alcohol"
              adjustFor="p"
              className="offense-substances"
              onUpdate={this.updateInvolvedAlcohol}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedAlcohol`}
            >
              {i18n.t('legal.pvq.police.label.alcohol')}
            </Branch>

            <Field
              title={i18n.t('legal.pvq.police.heading.description')} // 'Why were you charged, cited, summoned, or ticketed?',
              help="legal.pvq.police.help.description"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Description`}
            >
              <Textarea
                {...this.props.Description}
                name="Description"
                className="offense-description"
                onUpdate={this.updateDescription}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Description`}
              />
            </Field>

          </>
        )}

        <div>
          <Field
            title={i18n.t('legal.pvq.police.heading.citedagency')} // 'What is the name of the law enforcement agency involved? (Do not use acronyms.)',
            titleSize="h4"
            optional
            className="no-margin-bottom"
          />

          <Field
            title={i18n.t(citedByLabel)} // 'Provide the name of the law enforcement agency that arrested/cited/summoned you.',
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CitedBy`}
          >
            <Text
              {...this.props.CitedBy}
              name="CitedBy"
              className="offense-citedby"
              onUpdate={this.updateCitedBy}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.CitedBy`}
            />
          </Field>

          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyAddress`}
          >
            <PVQLocation
              {...this.props.AgencyAddress}
              name="AgencyAddress"
              attribute={`${attribute}.AgencyAddress`}
              label={i18n.t('legal.pvq.police.heading.agencyaddress')}
              domesticTitle={i18n.t('legal.pvq.police.label.agencyAddress')}
              foreignTitle={i18n.t('legal.pvq.police.label.agencyAddressForeign')}
              layout={Location.ADDRESS}
              geocode
              addressBook="Facility"
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateAgencyAddress}
              onError={this.props.onError}
              required={this.props.required}
              isPoBoxAllowed={false}
              showInternationalStreet={false}
            />
          </Field>
        </div>

        <Branch
          {...this.props.WasCharged}
          name="was_charged"
          label={i18n.t('legal.pvq.police.heading.charged')} // 'Were you arrested for this offense?',
          labelSize="h4"
          className="offense-charged  no-margin-bottom"
          onUpdate={this.updateWasCharged}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WasCharged`}
        />

        <Show
          when={(this.props.WasCharged || {}).value === 'Yes'}
        >
          <div style={{
            marginTop: '20px'
          }}
          >
            <Branch
              {...this.props.OtherLawEnforcementAgency}
              name="other_law_enforcement_agency"
              label={i18n.t('legal.pvq.police.heading.otherlawenforcementagency')} /* 'Did the same law enforcement agency you listed above arrest you?' */
              labelSize="h4"
              className="offense-charged  no-margin-bottom"
              onUpdate={this.updateOtherLawEnforcementAgency}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.OtherLawEnforcementAgency`}
            />
          </div>
        </Show>

        <Show when={this.props.WasCharged?.value === 'Yes'}>
          <Show when={this.props.OtherLawEnforcementAgency?.value === 'No'}> {/* No is it is a different law enforcement agency so let subject provide contact info */}
            <div style={{
              marginTop: '20px'
            }}
            >

              <Field
                title={i18n.t('legal.pvq.police.heading.offenseArrestingAgency')} // 'What is the name of the law enforcement agency that arrested you? (Do not use acronyms)'
                adjustFor="labels"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ArrestingAgencyName`}
                exactAttribute
              >
                <Text
                  {...this.props.ArrestingAgencyName}
                  name="ArrestingAgencyName"
                  className="offense-courtname"
                  onUpdate={this.updateArrestingAgencyName}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.ArrestingAgencyName`}
                />
              </Field>

              <PVQLocation
                {...this.props.ArrestingAgencyAddress}
                name="ArrestingAgencyAddress"
                attribute={`${attribute}.ArrestingAgencyAddress`}
                label={i18n.t('legal.pvq.police.heading.offenseArrestingAgencyAddress')}
                domesticTitle={i18n.t('legal.pvq.police.label.agencyAddress')}
                foreignTitle={i18n.t('legal.pvq.police.label.agencyAddressForeign')}
                layout={Location.ADDRESS}
                geocode
                addressBook="Facility"
                addressBooks={this.props.addressBooks}
                dispatch={this.props.dispatch}
                onUpdate={this.updateArrestingAgencyAddress}
                onError={this.props.onError}
                required={this.props.required}
                isPoBoxAllowed={false}
                showInternationalStreet={false}
              />
            </div>
          </Show>
        </Show>
        <div style={{ marginTop: '20px' }}>
          <Branch
            {...this.props.CourtStatus}
            name="court_status"
            label={i18n.t('legal.pvq.police.heading.offenseCourtStatus')} // 'Did you go to court or are you waiting to go to court?',
            labelSize="h4"
            className="offense-charged  "
            onUpdate={this.updateCourtStatus}
            required={this.props.required}
            onError={this.props.onError}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CourtStatus`}
          />
        </div>
        <Show when={this.props.CourtStatus?.value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('legal.pvq.police.heading.offenseCourtToAppear')} // 'What is the name of the court you appeared in or are to appear in? (Do not use acronyms)'
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtToAppear`}
              exactAttribute
            >
              <Text
                {...this.props.CourtToAppear}
                name="CourtToAppear"
                className="offense-court-to-Appear"
                onUpdate={this.updateCourtToAppear}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtToAppear`}
              />
            </Field>

            <Field
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtToAppearAddress`}
            >
              <PVQLocation
                {...this.props.CourtToAppearAddress}
                name="CourtToAppearAddress"
                attribute={`${attribute}.CourtToAppearAddress`}
                label={i18n.t('legal.pvq.police.heading.offenseCourtToAppearAddress')}
                domesticTitle={i18n.t('legal.pvq.police.label.courtAddressUS')}
                foreignTitle={i18n.t('legal.pvq.police.label.courtAddressForeign')}
                layout={Location.ADDRESS}
                geocode
                addressBook="Facility"
                addressBooks={this.props.addressBooks}
                dispatch={this.props.dispatch}
                onUpdate={this.updateCourtToAppearAddress}
                onError={this.props.onError}
                required={this.props.required}
                isPoBoxAllowed={false}
                showInternationalStreet={false}
              />
            </Field>
          </div>
        </Show>
        {this.props.CourtStatus?.value === 'No' && (
          <>
            <Branch
              {...this.props.CourtFinePayment}
              name="court_fine_payment"
              // Did you pay a fine?
              label={i18n.t('legal.pvq.police.heading.offenseCourtFinePayment')}
              labelSize="h4"
              className="offense-charged"
              onUpdate={this.updateCourtFinePayment}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtFinePayment`}
            />
            <Show when={(this.props.CourtFinePayment || {}).value === 'Yes'}>
              <Field
                // How much was this fine?
                title={i18n.t('legal.pvq.police.heading.offenseCourtFineAmount')}
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.CourtFineAmount`}
              >
                <Text
                  {...this.props.CourtFineAmount}
                  className="awaiting-trial-explanation"
                  name="offense_fine_amount"
                  onError={this.props.onError}
                  required={this.props.required}
                  onUpdate={this.updateCourtFineAmount}
                  attribute={`${attribute}.CourtFineAmount`}
                />
              </Field>
              <Branch
                {...this.props.CourtFinePaidInFull}
                name="court_fine_paid_in_full"
                // Did you pay this fine in full?
                label={i18n.t('legal.pvq.police.heading.offenseCourtFinePaidInFull')}
                labelSize="h4"
                className="offense-charged"
                onUpdate={this.updateCourtFinePaidInFull}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.CourtFinePaidInFull`}
              />
              <Show when={this.props.CourtFinePaidInFull?.value === 'Yes'}>
                <Field
                  // When did you pay this fine in full?
                  title={i18n.t('legal.pvq.police.heading.offenseCourtFinePaidInFullDate')}
                  help="legal.pvq.police.help.date"
                  adjustFor="labels"
                  shrink
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.CourtFinePaidInFullDate`}
                >
                  <DateControl
                    {...this.props.CourtFinePaidInFullDate}
                    name="court_fine_paid_in_full_date"
                    className="offense-date"
                    minDateEqualTo
                    onUpdate={this.updateCourtFinePaidInFullDate}
                    onError={this.props.onError}
                    required={this.props.required}
                    hideDay
                    attribute={`${attribute}.CourtFinePaidInFullDate`}
                  />
                </Field>
              </Show>
              <Show when={this.props.CourtFinePaidInFull?.value === 'No'}>
                <Field
                  // Please explain.
                  title={i18n.t('legal.pvq.police.label.offenseCourtFineNotPaidInFullExplanation')}
                  titleSize="h4"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.CourtFineNotPaidInFullExplanation`}
                >
                  <Textarea
                    {...this.props.CourtFineNotPaidInFullExplanation}
                    name="CourtFineNotPaidInFullExplanation"
                    className="offense-explanation"
                    onUpdate={this.updateCourtFineNotPaidInFullExplanation}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.CourtFineNotPaidInFullExplanation`}
                  />
                </Field>
              </Show>
            </Show>
          </>
        )}
        <div style={{ marginBottom: '8rem' }}>
          <Field
            title={i18n.t(chargesOuterTitle)}
            titleSize="h3"
            optional
            className="no-margin-bottom"
          >
            {i18n.m(chargesInnerTitle)}
          </Field>

          <Accordion
            appendTitle={i18n.t('legal.pvq.police.collection.appendTitleContinuation')}
            {...this.props.Charges}
            className="offense-charges"
            defaultState={this.props.defaultState}
            onUpdate={this.updateCharges}
            onError={this.props.onError}
            summary={this.summary}
            description={i18n.t('legal.pvq.police.collection.summary.title')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            errors={chargesErrors}
            attribute={`${attribute}.Charges`}
            ignoreInitialScrollTo
          >
            <Charge
              name="Item"
              bind
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              offenseDate={this.props.Date}
              formType={this.props.formType}
              attribute={`${attribute}.Charges`}
            />

          </Accordion>
        </div>
        {/** Moved from Sentence */}
        <Branch
          {...this.props.CaseClosed}
          name="CaseClosed"
          label={i18n.t('legal.pvq.police.heading.caseClosed')} // Is your case closed?
          labelSize="h4"
          className="probation"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateCaseClosed}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CaseClosed`}
        />
        <Show when={this.props.CaseClosed?.value === 'Yes'}>
          <Field
            title={i18n.t('legal.pvq.police.heading.caseClosedDate')} // 'When was your case closed?',
            adjustFor="labels"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CaseClosedDate`}
          >
            <DateControl
              {...this.props.CaseClosedDate}
              name="CaseClosedDate"
              className="case-closed-date"
              minDateEqualTo
              onUpdate={this.updateCaseClosedDate}
              onError={this.props.onError}
              required={this.props.required}
              hideDay
              attribute={`${attribute}.CaseClosedDate`}
            />
          </Field>
        </Show>
        <Show when={this.props.CaseClosed?.value === 'No'}>
          <Field
            title={i18n.t('legal.pvq.police.heading.caseClosedStatusExplanation')}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CaseClosedStatusExplanation`}
          >
            <Textarea
              {...this.props.CaseClosedStatusExplanation}
              className="case-closed-status-explanation"
              name="CaseClosedStatusExplanation"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateCaseClosedStatusExplanation}
              attribute={`${attribute}.CaseClosedStatusExplanation`}
            />
          </Field>
        </Show>
        <br />
      </div>
    ) : (
      <div className="offense">

        <Field
          title={i18n.t('legal.police.heading.date')}
          help="legal.police.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            className="offense-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.police.heading.description')}
          help="legal.police.help.description"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            className="offense-description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        {requireLegalOffenseInvolvements && (
          <span>
            <Branch
              name="involved_violence"
              label={i18n.t('legal.police.heading.involvement')}
              labelSize="h4"
              adjustFor="p"
              className="offense-violence no-margin-bottom"
              {...this.props.InvolvedViolence}
              onUpdate={this.updateInvolvedViolence}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedViolence`}
            >
              {i18n.t('legal.police.label.violence')}
            </Branch>

            <Branch
              name="involved_firearms"
              adjustFor="p"
              className="offense-firearms no-margin-bottom"
              {...this.props.InvolvedFirearms}
              onUpdate={this.updateInvolvedFirearms}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedFirearms`}
            >
              {i18n.t('legal.police.label.firearms')}
            </Branch>

            <Branch
              name="involved_substances"
              adjustFor="p"
              className="offense-substances"
              {...this.props.InvolvedSubstances}
              onUpdate={this.updateInvolvedSubstances}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedSubstances`}
            >
              {i18n.t('legal.police.label.substances')}
            </Branch>
          </span>
        )}

        <Field
          title={i18n.t('legal.police.heading.address')}
          optional
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            className="offense-address"
            label={i18n.t('legal.police.label.address')}
            layout={Location.OFFENSE}
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Branch
          name="was_cited"
          label={i18n.t('legal.police.heading.cited')}
          labelSize="h4"
          adjustFor="p"
          className="offense-cited"
          {...this.props.WasCited}
          onUpdate={this.updateWasCited}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WasCited`}
        />

        <Show when={(this.props.WasCited || {}).value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('legal.police.heading.citedagency')}
              titleSize="h4"
              optional
              className="no-margin-bottom"
            />
            <Field
              title={i18n.t('legal.police.heading.citedby')}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CitedBy`}
            >
              <Text
                name="CitedBy"
                {...this.props.CitedBy}
                className="offense-citedby"
                onUpdate={this.updateCitedBy}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CitedBy`}
              />
            </Field>

            <Field
              title={i18n.t('legal.police.heading.agencyaddress')}
              optional
              help="legal.police.help.agencyaddress"
              adjustFor="address"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AgencyAddress`}
            >
              <Location
                name="AgencyAddress"
                {...this.props.AgencyAddress}
                className="offense-agencyaddress"
                label={i18n.t('legal.police.label.address')}
                layout={Location.OFFENSE}
                dispatch={this.props.dispatch}
                onUpdate={this.updateAgencyAddress}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.AgencyAddress`}
              />
            </Field>
          </div>
        </Show>

        <Show
          when={
            (this.props.WasCited || {}).value === 'No'
            || (this.props.WasCited || {}).value === 'Yes'
          }
        >
          <Branch
            name="was_charged"
            label={i18n.t('legal.police.heading.charged')}
            labelSize="h4"
            className="offense-charged  no-margin-bottom"
            {...this.props.WasCharged}
            onUpdate={this.updateWasCharged}
            required={this.props.required}
            onError={this.props.onError}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WasCharged`}
          />
        </Show>

        <Show when={(this.props.WasCharged || {}).value === 'No'}>
          <Field
            title={i18n.t('legal.police.label.explanation')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              name="Explanation"
              {...this.props.Explanation}
              className="offense-explanation"
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>
        </Show>

        <Show when={(this.props.WasCharged || {}).value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('legal.police.heading.courtname')}
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtName`}
            >
              <Text
                name="CourtName"
                {...this.props.CourtName}
                className="offense-courtname"
                onUpdate={this.updateCourtName}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtName`}
              />
            </Field>

            <Field
              title={i18n.t('legal.police.heading.courtaddress')}
              optional
              help="legal.police.help.courtaddress"
              adjustFor="address"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtAddress`}
            >
              <Location
                name="CourtAddress"
                {...this.props.CourtAddress}
                label={i18n.t('legal.police.label.address')}
                className="offense-courtaddress"
                geocode
                layout={Location.OFFENSE}
                addressBooks={this.props.addressBooks}
                addressBook="Court"
                dispatch={this.props.dispatch}
                onUpdate={this.updateCourtAddress}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtAddress`}
              />
            </Field>

            <div style={{ marginBottom: '8rem' }}>
              <Field
                title={i18n.t('legal.police.heading.chargedetails')}
                titleSize="h3"
                optional
                className="no-margin-bottom"
              >
                {i18n.m('legal.police.para.chargedetails')}
              </Field>

              <Accordion
                className="offense-charges"
                {...this.props.Charges}
                defaultState={this.props.defaultState}
                onUpdate={this.updateCharges}
                onError={this.props.onError}
                summary={this.summary}
                description={i18n.t('legal.police.collection.summary.title')}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                errors={chargesErrors}
                attribute={`${attribute}.Charges`}
                ignoreInitialScrollTo
              >
                <Charge
                  name="Item"
                  bind
                  dispatch={this.props.dispatch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  offenseDate={this.props.Date}
                  attribute={`${attribute}.Charges`}
                />
              </Accordion>
            </div>

            <Branch
              name="was_sentenced"
              label={i18n.t('legal.police.heading.sentenced')}
              labelSize="h4"
              className="offense-sentenced"
              {...this.props.WasSentenced}
              onUpdate={this.updateWasSentenced}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.WasSentenced`}
            />

            <Show when={(this.props.WasSentenced || {}).value === 'Yes'}>
              <div>
                <Sentence
                  name="Sentence"
                  {...this.props.Sentence}
                  onError={this.props.onError}
                  required={this.props.required}
                  onUpdate={this.updateSentence}
                  scrollIntoView={this.props.scrollIntoView}
                  requireLegalOffenseSentenced={requireLegalOffenseSentenced}
                  requireLegalOffenseIncarcerated={requireLegalOffenseIncarcerated}
                  attribute={`${attribute}.Sentence`}
                />
              </div>
            </Show>

            <Show when={(this.props.WasSentenced || {}).value === 'No'}>
              <div>
                <Branch
                  name="awaiting_trial"
                  label={i18n.t('legal.police.heading.awaitingTrial')}
                  labelSize="h4"
                  className="awaiting-trial no-margin-bottom"
                  {...this.props.AwaitingTrial}
                  onError={this.props.onError}
                  required={this.props.required}
                  onUpdate={this.updateAwaitingTrial}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.AwaitingTrial`}
                />
                <Field
                  title={i18n.t('legal.police.heading.awaitingTrialExplanation')}
                  titleSize="h4"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.AwaitingTrialExplanation`}
                >
                  <Textarea
                    className="awaiting-trial-explanation"
                    {...this.props.AwaitingTrialExplanation}
                    name="awaiting_trial_explanation"
                    onError={this.props.onError}
                    required={this.props.required}
                    onUpdate={this.updateAwaitingTrialExplanation}
                    attribute={`${attribute}.AwaitingTrialExplanation`}
                  />
                </Field>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    );
  }
}

Offense.defaultProps = {
  Date: {},
  Description: {},
  InvolvedViolence: {},
  InvolvedFirearms: {},
  InvolvedSubstances: {},
  InvolvedExplosives: {}, // PVQ Only
  InvolvedAlcohol: {}, // PVQ Only
  OtherLawEnforcementAgency: {}, // PVQOnly
  Address: {},
  WasCited: {},
  CitedBy: {},
  AgencyAddress: {},
  WasCharged: {},
  Explanation: {},
  CourtName: {},
  CourtAddress: {},
  Charges: { items: [] },
  WasSentenced: {},
  Sentence: {},
  AwaitingTrial: {},
  AwaitingTrialExplanation: {},
  addressBooks: {},
  CourtStatus: {}, // PVQOnly
  CourtFinePayment: {}, // PVQOnly
  CourtFineAmount: {}, // PVQOnly
  CourtFinePaidInFull: {}, // PVQOnly
  CourtFinePaidInFullDate: {}, // PVQOnly
  CourtFineNotPaidInFullExplanation: {}, // PVQOnly
  CourtToAppear: {}, // PVQOnly
  CourtToAppearAddress: {}, // PVQOnly
  HasFelonyMoreThanXYears: {}, // PVQONly - Part B Continuation
  HasChargesInvolvingFirearmsMoreThanXYears: {}, // PVQONly - Part B Continuation
  HasChargesInvolvingDomesticViolenceMoreThanXYears: {}, // PVQONly - Part B Continuation
  HasCitationLastXYears: {}, // PVQ Only
  HasChargesLastXYears: {}, // PVQ Only
  HasProbationLastXYears: {}, // PVQ Only
  HasParoleLastXYears: {}, // PVQ Only
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
};
